// A single review thread — a root comment, its replies, a reply composer, and
// reaction bars. Pure view: all data + mutations arrive as props (the sidebar
// and the board-pin popover both render it). `outdated` is passed in by the
// caller (derived live from the diagram ChangeSet for node anchors).
import { FormEvent, useState } from "react";
import { Button } from "../ui/Button.js";
import { useContextMenu, type ContextMenuItem } from "../ui/ContextMenu";
import { ReactionBar } from "./ReactionBar.js";
import type { ReviewComment } from "./proposalsApi.js";
import { useTranslation } from "../i18n";

export interface CommentThreadHandlers {
  readonly onReply: (rootId: string, body: string) => void;
  readonly onResolve: (rootId: string, resolved: boolean) => void;
  readonly onDelete: (commentId: string) => void;
  readonly onReact: (commentId: string, emoji: string) => void;
}

export function CommentThread({
  comment,
  myId,
  outdated = false,
  busy = false,
  handlers,
}: {
  readonly comment: ReviewComment;
  readonly myId: string | null;
  readonly outdated?: boolean;
  readonly busy?: boolean;
  readonly handlers: CommentThreadHandlers;
}) {
  const { t } = useTranslation("proposals");
  return (
    <div
      className={`rounded-md border bg-slate-950/50 p-3 ${
        comment.resolved ? "border-slate-800/60 opacity-70" : "border-slate-800"
      }`}
    >
      <CommentCard comment={comment} myId={myId} handlers={handlers}>
        {outdated && (
          <span className="rounded-sm bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-300">
            {t("thread.outdated")}
          </span>
        )}
        {comment.resolved && (
          <span className="rounded-sm bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-300">
            {t("thread.resolved")}
          </span>
        )}
      </CommentCard>

      {comment.replies.length > 0 && (
        <div className="mt-2 space-y-2 border-l border-slate-800 pl-3">
          {comment.replies.map((r) => (
            <CommentCard key={r.id} comment={r} myId={myId} handlers={handlers} />
          ))}
        </div>
      )}

      <div className="mt-2 flex items-center justify-between gap-2">
        <ReplyComposer disabled={busy} onSubmit={(body) => handlers.onReply(comment.id, body)} />
        <button
          type="button"
          disabled={busy}
          onClick={() => handlers.onResolve(comment.id, !comment.resolved)}
          className="shrink-0 text-xs text-slate-400 hover:text-slate-200 disabled:opacity-50"
        >
          {comment.resolved ? t("thread.unresolve") : t("thread.resolve")}
        </button>
      </div>
    </div>
  );
}

function CommentCard({
  comment,
  myId,
  handlers,
  children,
}: {
  readonly comment: ReviewComment;
  readonly myId: string | null;
  readonly handlers: CommentThreadHandlers;
  readonly children?: React.ReactNode;
}) {
  const { t } = useTranslation("proposals");
  const menu = useContextMenu();
  const canDelete = comment.authorUserId === myId;
  const items: ContextMenuItem[] = [
    { label: t("thread.copyText"), onClick: () => void navigator.clipboard?.writeText(comment.body) },
    ...(canDelete
      ? [{ label: t("thread.delete"), onClick: () => handlers.onDelete(comment.id), danger: true, separator: true }]
      : []),
  ];
  return (
    <div onContextMenu={(e) => menu.open(e, items)}>
      <div className="mb-1 flex items-center gap-2">
        <span className="text-xs font-medium text-slate-300">{comment.authorName ?? t("thread.someone")}</span>
        {children}
        {canDelete && (
          <button
            type="button"
            className="ml-auto text-xs text-slate-500 hover:text-red-300"
            onClick={() => handlers.onDelete(comment.id)}
          >
            {t("thread.delete")}
          </button>
        )}
      </div>
      <p className="whitespace-pre-wrap break-words text-sm text-slate-200">{comment.body}</p>
      <div className="mt-1.5">
        <ReactionBar
          reactions={comment.reactions}
          onToggle={(emoji) => handlers.onReact(comment.id, emoji)}
        />
      </div>
      {menu.node}
    </div>
  );
}

function ReplyComposer({
  onSubmit,
  disabled,
}: {
  readonly onSubmit: (body: string) => void;
  readonly disabled?: boolean;
}) {
  const { t } = useTranslation("proposals");
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");

  if (!open) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className="text-xs text-slate-400 hover:text-slate-200 disabled:opacity-50"
      >
        {t("thread.reply")}
      </button>
    );
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    const body = draft.trim();
    if (!body) return;
    onSubmit(body);
    setDraft("");
    setOpen(false);
  }

  return (
    <form className="flex-1" onSubmit={submit}>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={2}
        autoFocus
        placeholder={t("thread.replyPlaceholder")}
        aria-label={t("thread.replyAria")}
        className="w-full resize-none rounded-md border border-slate-700 bg-slate-950 p-2 text-sm text-slate-200 placeholder:text-slate-600 focus:border-slate-500 focus:outline-none"
      />
      <div className="mt-1 flex justify-end gap-2">
        <button
          type="button"
          className="text-xs text-slate-400 hover:text-slate-200"
          onClick={() => {
            setOpen(false);
            setDraft("");
          }}
        >
          {t("thread.replyCancel")}
        </button>
        <Button type="submit" variant="primary" size="sm" disabled={!draft.trim() || disabled}>
          {t("thread.replySubmit")}
        </Button>
      </div>
    </form>
  );
}
